import { useEffect, useState } from 'react';
import { useShow, useDelete } from '@refinedev/core';
import { useLocation, useNavigate, useParams } from 'react-router';
import type { TfTrackRow } from '@verybigthings/trackframe-shared/schemas';
import { trackLabel } from '@verybigthings/trackframe-shared/tracks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageHeader } from '@/components/PageHeader';
import { FieldList } from '@/components/FieldList';
import { fmtIso } from '@/lib/format';
import { AccessTab } from './access/AccessTab';
import { RolesTab } from './roles/RolesTab';
import { TrackSettingsPanel } from './settings/TrackSettingsPanel';

// Tab-name segment the App.tsx route allows after `show/:id/` (HUB-109 /
// TAZ-07). Unknown segments fall back to the overview.
export const TRACK_TABS = ['overview', 'access', 'roles', 'settings'] as const;
export type TrackTab = (typeof TRACK_TABS)[number];

function isTrackTab(value: string): value is TrackTab {
  return (TRACK_TABS as readonly string[]).includes(value);
}

export function tabFromPath(pathname: string, trackId: string | undefined): TrackTab {
  if (!trackId) return 'overview';
  const base = `/admin/tracks/show/${trackId}`;
  if (!pathname.startsWith(base)) return 'overview';
  const segment = pathname.slice(base.length).replace(/^\/+|\/+$/g, '');
  return isTrackTab(segment) ? segment : 'overview';
}

export function tabPath(trackId: string, tab: TrackTab): string {
  const base = `/admin/tracks/show/${trackId}`;
  return tab === 'overview' ? base : `${base}/${tab}`;
}

/** The four triggers — exported so the order is pinned by a static render. */
export function TrackTabsList() {
  return (
    <TabsList>
      <TabsTrigger value="overview">Overview</TabsTrigger>
      <TabsTrigger value="access">Access</TabsTrigger>
      <TabsTrigger value="roles">Roles</TabsTrigger>
      <TabsTrigger value="settings">Settings</TabsTrigger>
    </TabsList>
  );
}

function ConfigurationCard({ track }: { track: TfTrackRow }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Configuration</CardTitle>
      </CardHeader>
      <CardContent>
        <FieldList
          fields={[
            { label: 'ID', value: track.id, mono: true },
            { label: 'Name (slug)', value: track.name, mono: true },
            { label: 'API URL', value: track.apiUrl, mono: true },
            {
              label: 'Frontend URL',
              mono: true,
              value: track.baseUrl ? (
                <a href={track.baseUrl} target="_blank" rel="noreferrer" className="underline">
                  {track.baseUrl}
                </a>
              ) : (
                '—'
              ),
            },
            { label: 'Health-check URL', value: track.healthCheckUrl ?? '—', mono: true },
            { label: 'OpenAPI', value: track.openapiSpecJson ? 'present' : '—' },
            { label: 'Created', value: fmtIso(track.createdAt) },
            { label: 'Updated', value: fmtIso(track.updatedAt) },
          ]}
        />
      </CardContent>
    </Card>
  );
}

export function TracksShow() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  // Resource + id passed explicitly: the component also mounts under
  // `show/:id/:tab`, which does not match the registered resource path
  // (same reason as users/Show.tsx).
  const { query } = useShow<TfTrackRow>({ resource: 'tracks', id: params.id });
  const track = query.data?.data;
  const { mutate: deleteTrack, mutation: deleteMutation } = useDelete();
  const deleting = deleteMutation.isPending;
  const [confirmDelete, setConfirmDelete] = useState(false);

  // URL <-> tab sync; a tab change pushes a history entry so Back toggles tabs.
  const [tab, setTab] = useState<TrackTab>(() => tabFromPath(location.pathname, params.id));
  useEffect(() => {
    setTab(tabFromPath(location.pathname, params.id));
  }, [location.pathname, params.id]);
  const handleTabChange = (next: string) => {
    const value = isTrackTab(next) ? next : 'overview';
    setTab(value);
    if (!params.id) return;
    const target = tabPath(params.id, value);
    if (location.pathname !== target) navigate(target, { replace: false });
  };

  if (query.isLoading) return <div>Loading…</div>;
  if (!track) return <div>Not found.</div>;

  const onDelete = () =>
    deleteTrack(
      { resource: 'tracks', id: track.id },
      {
        onSuccess: () => navigate('/admin/tracks'),
      },
    );

  return (
    <div className="space-y-4">
      <PageHeader
        title={trackLabel(track)}
        description={track.description ?? '—'}
        actions={[
          { label: 'Edit', to: `/admin/tracks/edit/${track.id}`, variant: 'outline' },
          { label: 'Delete', variant: 'destructive', onClick: () => setConfirmDelete(true), disabled: deleting },
        ]}
      />

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TrackTabsList />
        <TabsContent value="overview">
          <ConfigurationCard track={track} />
        </TabsContent>
        <TabsContent value="access">
          <AccessTab trackId={track.id} trackName={trackLabel(track)} />
        </TabsContent>
        <TabsContent value="roles">
          <RolesTab trackId={track.id} />
        </TabsContent>
        <TabsContent value="settings">
          <TrackSettingsPanel trackId={track.id} />
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete track?"
        description={`"${track.name}" will be removed. Sessions referencing it will keep history but lose routing.`}
        confirmLabel="Delete"
        destructive
        busy={deleting}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={onDelete}
      />
    </div>
  );
}
